/**
 * Theme Entry Point
 * Native ES modules with dynamic imports for lazy-loading sections
 * Migrated from Slate/Webpack
 */

// Core imports (always loaded)
import Sections from '@theme/core-sections';

// Create sections manager
const sections = new Sections();

/**
 * Section loaders - dynamic imports for lazy loading
 * Each section is only loaded when it appears on the page or in the theme editor
 */
const sectionLoaders = {
  // Static sections (template-specific)
  'static-product': () => import('@theme/section-static-product'),
  'static-product-three-col': () => import('@theme/section-product-three-col'),
  'static-product-details': () => import('@theme/section-static-product-details'),
  'static-cart': () => import('@theme/section-static-cart'),
  'static-collection': () => import('@theme/section-static-collection'),
  'static-collection-minimal': () => import('@theme/section-static-collection-minimal'),
  'static-header': () => import('@theme/section-static-header'),
  'static-footer': () => import('@theme/section-static-footer'),
  'static-article': () => import('@theme/section-static-article'),
  'static-blog': () => import('@theme/section-static-blog'),
  'static-search': () => import('@theme/section-static-search'),
  'static-password': () => import('@theme/section-static-password'),
  'static-announcement': () => import('@theme/section-static-announcement'),
  'static-utility-bar': () => import('@theme/section-static-utility-bar'),
  'static-slideshow': () => import('@theme/section-static-slideshow'),
  'static-recently-viewed': () => import('@theme/section-static-recently-viewed'),
  'static-product-reviews': () => import('@theme/section-static-product-reviews'),
  'static-product-recommendations': () => import('@theme/section-static-product-recommendations'),

  // Dynamic sections (theme editor configurable)
  'dynamic-video': () => import('@theme/section-dynamic-video'),
  'dynamic-featured-collection': () => import('@theme/section-dynamic-featured-collection'),
  'dynamic-featured-product': () => import('@theme/section-static-product'), // Uses same handler
  'featured-collections-slider': () => import('@theme/section-dynamic-featured-collections-slider'),
  'dynamic-blog-posts': () => import('@theme/section-dynamic-blog-posts'),
  'dynamic-collection-list': () => import('@theme/section-dynamic-collection-list'),
  'dynamic-highlights-banners': () => import('@theme/section-dynamic-highlights-banners'),
  'dynamic-image-with-text': () => import('@theme/section-dynamic-image-with-text'),
  'dynamic-map': () => import('@theme/section-dynamic-map'),
  'dynamic-menu-list': () => import('@theme/section-dynamic-menu-list'),
  'dynamic-newsletter': () => import('@theme/section-dynamic-newsletter'),
  'dynamic-promo-grid': () => import('@theme/section-dynamic-promo-blocks'),
  'dynamic-promo-mosaic': () => import('@theme/section-dynamic-promo-blocks'),
  'dynamic-rich-text': () => import('@theme/section-dynamic-rich-text'),
  'dynamic-search': () => import('@theme/section-dynamic-search'),
  'dynamic-smart-search': () => import('@theme/section-dynamic-smart-search'),
  'dynamic-testimonials': () => import('@theme/section-dynamic-testimonials'),
  'dynamic-twitter-feed': () => import('@theme/section-dynamic-twitter-feed'),
};

// Register all sections with lazy loading
Object.entries(sectionLoaders).forEach(([type, loader]) => {
  sections.registerLazy(type, loader);
});

/**
 * Reload section helper - useful for AJAX page transitions
 */
function reloadSection(sectionId) {
  const target = document.querySelector(`[data-section-type="${sectionId}"]`);
  if (!target) return;

  const parentTarget = target.parentNode;

  sections._onSectionEvent({
    target: parentTarget,
    detail: { sectionId },
    type: 'shopify:section:unload'
  });

  sections._onSectionEvent({
    target: parentTarget,
    detail: { sectionId },
    type: 'shopify:section:load'
  });
}

// Listen for PDP changes (AJAX product switching)
window.addEventListener('pdp:change', () => {
  document.querySelectorAll('.site-main [data-section-type]').forEach(el => {
    reloadSection(el.dataset.sectionType);
  });

  window.StampedFn && window.StampedFn.reloadUGC();
});

// Initialize page-specific functionality
function initPageSpecific() {
  // Gift card page
  if (document.body.classList.contains('giftcard')) {
    import('@theme/page-gift-card').then(module => new module.default());
  }

  // Account pages
  if (document.body.classList.contains('template-login') || document.body.classList.contains('template-account')) {
    import('@theme/page-account').then(module => new module.default()).catch(() => { });
  }

  if (document.body.classList.contains('template-page')) {
    // Contact page
    import('@theme/page-contact').then(module => new module.default()).catch(() => { });
    // Generic page template
    import('@theme/page-page').then(module => new module.default()).catch(() => { });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPageSpecific);
} else {
  initPageSpecific();
}

// Intercom chat link handler
document.addEventListener('click', (e) => {
  if (e.target.matches('a[href="#chat"]')) {
    e.preventDefault();
    window?.GorgiasChat?.open();
  }
});

// Export for debugging
window.Theme = window.Theme || {};
window.Theme.sections = sections;
